import { useCallback, useState, useEffect } from "react";
import { useLocation, Route, Switch, Redirect } from "react-router-dom";

import Box from "@material-ui/core/Box";

import AdminNavbar from "./components/navbar";
import Sidebar from "components/_refactor/sidebar/page";

import { useRoutes } from "hooks/useRoutes";
import authentication from "hooks/authentication.js";
import {
  getUserNotifications,
  getUserAllNotifications,
} from "components/Notification/Notifications";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import { updateUserFirebaseId } from "components/Notification/Notifications";
import { getNotificationsPermission, onMessageListener } from "lib/firebase";
import Home from "views/home/page";
import NeoAssist from "components/Widgets/NeoAssist";
import { DialogLastRelease } from "components/Dialogs/DialogLastRelease";
import Api from "lib/api/httpRequestHelper";
import InactivityTimeout from "hooks/inactivityTimeout";
import { IsSmall } from "utils/is-mobile";
import DialogPopUpInfo from "./components/popup-info";

// Telas que ocupam a janela inteira: continuam sob /admin (para manter permissão,
// item de menu e rota), mas são renderizadas sem a sidebar e o header do portal.
const FULLSCREEN_ROUTES = ["/admin/vitrine-virtual-b2b"];

export default function Admin() {
  const location = useLocation();
  const snackbar = useSnackbar();
  const { routesVision } = useRoutes();

  const [sidebarOpenResponsive, setSidebarOpenResponsive] = useState(true);
  const [userNotifications, setUserNotifications] = useState(null);
  const [userAllNotifications, setUserAllNotifications] = useState(null);
  const [userPopUps, setUserPopUps] = useState(null);
  const [openPopUp, setOpenPopUp] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [releases, setReleases] = useState(null);
  const [userRoutes, setUserRoutes] = useState(routesVision);

  const isSmall = IsSmall();

  const getModalContent = async () => {
    try {
      const response = await Api.post(
        "verify-notification-user",
        "conteudo",
        {},
        true
      );
      const list = response?.Result?.ListaDocumento.filter(
        (item) => item?.TipoMidia === "POPUP" && item?.NotificaNoAcesso === true
      );

      setUserPopUps(list);
      setOpenPopUp(list?.length > 0);
    } catch (error) {
      console.log(error);
    }
  };

  const handleUserNotifications = useCallback(async () => {
    getUserNotifications().then((res) => {
      setUserNotifications(res.Result);
    });
    getUserAllNotifications().then((res) => {
      setUserAllNotifications(res.Result);
    });
  }, []);

  useEffect(() => {
    if (routesVision) {
      setUserRoutes(routesVision);
    }
  }, [routesVision]);

  useEffect(() => {
    onMessageListener(authentication.userLoggedIn)
      .then(() => {
        handleUserNotifications();
      })
      .catch((error) => {
        // Silently handle errors - messaging may not be supported in webviews
        console.log("Firebase Messaging listener não disponível:", error);
      });
  }, []);

  const updateUserNotificationId = useCallback(
    async (idFirebase) => {
      return updateUserFirebaseId(idFirebase).catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
      });
    },
    [snackbar]
  );

  const handleGetReleases = async () => {
    await Api.get("release-product-list", "conteudo", `?page=0`, true)
      .then(({ Success, Result }) => {
        if (Success) {
          const newList = [];
          Result?.Releases?.map((item) => {
            if (item?.IdSistemaIntegracao === "PORTAL") {
              newList.push(item);
            }
          });
          setReleases(newList);
        }
      })
      .catch((error) => {
        if (localStorage.getItem("loggedIn")) {
          snackbar.showMessage(error, {
            severity: "error",
          });
        }      });
  };

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [location]);

  useEffect(() => {
    if (authentication.userLoggedIn) {
      getModalContent();
      getNotificationsPermission()
        .then((currentToken) => {
          if (currentToken) {
            localStorage.setItem("userFirebaseId", currentToken);
            updateUserNotificationId(currentToken);
          }
        })
        .catch((error) => {
          // Silently handle errors - notifications may not be supported in webviews
          console.log("Permissão de notificações não disponível:", error);
        });

      handleUserNotifications();

      const userNotificationsAutoRefresh = setInterval(
        handleUserNotifications,
        900000 // 15 minutos
      );

      return () => {
        clearInterval(userNotificationsAutoRefresh);
      };
    }
  }, [handleUserNotifications]);

  const getRoutes = (userSelectedRoutes) => {
    if (authentication.userLoggedIn !== true) {
      return (
        <Redirect
          to={{
            pathname: "/auth/login",
            state: { redirectReason: "userNotLoggedIn", location },
          }}
        />
      );
    }

    return userSelectedRoutes?.map((prop, key) => {
      if (prop.collapse) {
        if (prop.invisible) {
          return null;
        }
        return getRoutes(prop.views);
      }

      if (prop.layout === "/admin" && prop.path && prop.component) {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      } else {
        return null;
      }
    });
  };

  useEffect(() => {
    handleGetReleases();
  }, []);

  const isFullscreenRoute = FULLSCREEN_ROUTES.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  const routesSwitch = (
    <Switch>
      {getRoutes(userRoutes)}
      <Route path={"/home"} component={Home} key={"home"} />
      <Redirect from="/" to="/home" />
    </Switch>
  );

  return (
    <>
      {isFullscreenRoute ? (
        routesSwitch
      ) : (
        <Box>
          <Sidebar
            routes={userRoutes}
            isOpen={sidebarOpenResponsive}
            toggleSidebar={() =>
              setSidebarOpenResponsive(!sidebarOpenResponsive)
            }
            onClose={() => setSidebarOpenResponsive(true)}
          />

          <Box
            style={{
              marginLeft: isSmall
                ? "0"
                : sidebarOpenResponsive
                ? "60px"
                : "250px",
            }}
          >
            <AdminNavbar
              toggleSidebar={() =>
                setSidebarOpenResponsive(!sidebarOpenResponsive)
              }
              setOpenDialog={setOpenDialog}
              userNotifications={userNotifications}
              userAllNotifications={userAllNotifications}
            />

            {routesSwitch}
            <NeoAssist />
          </Box>
        </Box>
      )}
      {!isFullscreenRoute && openDialog && releases?.length !== 0 && (
        <DialogLastRelease
          setReleases={setReleases}
          releases={releases}
          open={openDialog}
          setOpenDialog={setOpenDialog}
        />
      )}
      {!isFullscreenRoute && userPopUps?.length > 0 && openPopUp && (
        <DialogPopUpInfo
          infos={userPopUps[0]}
          open={openPopUp}
          setOpenDialog={() => setOpenPopUp(false)}
        />
      )}
      <InactivityTimeout />
    </>
  );
}
